import { ChangeDetectorRef, Component, EventEmitter, Output } from "@angular/core";
import {
  addMonths,
  areIntervalsOverlapping,
  endOfMonth,
  isAfter,
  isBefore,
  startOfMonth,
  startOfYear,
  isSameMonth,
  startOfDay,
} from "@vattenfall/util";

import { endOfDay } from "date-fns";

import { CalendarDirective, DateRange } from "../calendar.directive";

@Component({
  selector: "ui-months",
  templateUrl: "./months.component.html",
  styles: [":host { width: 100% }"],
})
export class MonthsComponent extends CalendarDirective {
  /**
   * Outputs the selected month range from the calendar.
   */
  @Output()
  readonly selectMonthEvent = new EventEmitter<DateRange>();

  constructor(public changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }
  /**
   * Setup 12 element array - to store 12 dates for each month
   */
  get months(): Date[] {
    const activeOrSelectedDay: DateRange = this.active ? this.active : new DateRange(new Date());
    // Create an array of 12 empty spaces to be filled with month start dates.
    const months = Array(12).fill(null);
    // Get the first day of the year for the current active or selected day.
    const firstDayOfYear = startOfYear(activeOrSelectedDay.start);
    // For each empty record create the first of the month, each index.
    // return example [1 Jan yyyy, 1 Feb yyyy ..... , 1 Dec yyyy];
    return months.map((month, index) => new Date(addMonths(firstDayOfYear, index)));
  }

  /**
   * This function verifies whether the given month falls within the specified min and max date inputs.
   * A month is considered disabled only when all of its days fall within the disabled date ranges.
   * If the month contains at least one day available for selection, it will be displayed as an option.
   * @param month - The month to check for disabled status.
   * @returns a boolean value.
   */
  isDisabled(month: Date): boolean {
    const monthRange = new DateRange(startOfMonth(month), endOfMonth(month));
    const isMonthRangeOverlapping = areIntervalsOverlapping(monthRange, { start: startOfDay(this.min), end: endOfDay(this.max) });
    /**
     * If the month range start is before the minimum date, it returns the minimum date.
     * Otherwise, it returns the month range start.
     */
    const getStartDate = startOfDay(isBefore(monthRange.start, this.min) ? this.min : monthRange.start);
    const isDateWithinDisabledRanges = this.disabledDateRanges.some(
      (range: DateRange) =>
        (isBefore(range.start, getStartDate) || isSameMonth(range.start, getStartDate)) && isAfter(range.end, monthRange.end)
    );

    return !isMonthRangeOverlapping || isDateWithinDisabledRanges;
  }

  /**
   * Checks if a given date belongs to the currently selected range
   * @param startOfMonth the date to check
   * @returns boolean value if the date is in the same month as the selected range
   */
  isSelected(month: Date): boolean {
    // Return false if there is no selection yet
    if (!this.selected) return false;
    const { start, end } = this.selected;
    // Check if the selection spans across months
    if (isAfter(end, endOfMonth(month)) && isBefore(start, startOfMonth(addMonths(month, 1)))) {
      return true;
    }

    // Check if the selection is within the current month
    const monthRange = new DateRange(startOfMonth(month), endOfMonth(month));
    return isSameMonth(monthRange.start, start) && isSameMonth(monthRange.end, end);
  }
  /**
   * Will trigger the output with the selected month range
   * @param month - the date to buid the month around
   */
  selectMonthRange(month: Date): void {
    const monthRange = new DateRange(startOfMonth(month), endOfMonth(month));
    this.selectMonthEvent.emit(monthRange);
  }
}
